// Spoken cues for the routine player. This is the seam that keeps the player
// ignorant of *how* audio is produced. Today it's backed by the browser's
// Web Speech API (zero backend, zero cost); a future implementation could play
// pre-rendered TTS clips from S3 behind this same interface without the player
// changing at all.

import { resolveVoice } from './voices';

// Web Speech won't start speaking unless speak() has run inside a real user
// gesture. Cold-launching the app and tapping "Play" navigates to the player,
// whose first cue fires in a mount effect — outside any gesture — so it stays
// silent until the user taps again. Priming here, from the Play click itself,
// unlocks the engine for the whole session. Idempotent.
let speechPrimed = false;
export function primeSpeech(): void {
  if (speechPrimed) return;
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  speechPrimed = true;
  try {
    const synth = window.speechSynthesis;
    synth.getVoices(); // kick off async voice loading as early as possible
    synth.cancel();
    synth.resume(); // some engines cold-start paused
    synth.speak(new SpeechSynthesisUtterance(' '));
  } catch {
    /* best effort */
  }
}

export interface StepCue {
  name: string;
  /** Clip id for the pre-rendered player (pose id, or "other-side"). */
  clipId?: string;
  holdSeconds: number;
  index: number; // 0-based
  total: number;
}

export interface RoutineAnnouncer {
  /**
   * Prime the audio engine from within a user gesture. iOS/Safari refuses to
   * speak unless speak() has been called once during a real interaction, so the
   * player calls this on the first tap. Idempotent and safe to call repeatedly.
   */
  unlock(): void;
  /**
   * Warm a clip so its first play is instant (fetch + decode ahead of time).
   * No-op for engines that synthesize on the fly (Web Speech / silent).
   */
  prefetch(clipId: string): void;
  /**
   * Announce a pose as it becomes active. Interrupts any pending speech.
   * `onBegin` fires the instant the spoken "Begin." lands (or on speech error)
   * so the caller can start the hold clock only once the intro has been read —
   * keeping the timer honest. Callers must have their own fallback for the case
   * where no speech happens at all (e.g. audio off). `pauseMs` overrides the
   * gap before "Begin" (e.g. a longer pause when switching sides).
   */
  announceStep(cue: StepCue, onBegin?: () => void, pauseMs?: number): void;
  /** Periodic "N seconds to go" milestone during a hold. */
  announceTimeRemaining(seconds: number): void;
  /** Bare number for the final "3, 2, 1" tick-down. */
  /** The final spoken "3, 2, 1" as one smooth cue. */
  announceCountdown(): void;
  /**
   * Gentle "and release" as a pose ends. `onEnd` fires when the utterance
   * finishes (or on error / a safety fallback) so the caller can time a pause
   * from the end of speech rather than the start.
   */
  announceRelease(onEnd?: () => void): void;
  /** Announce that the whole routine is finished. */
  announceComplete(): void;
  /** Stop anything queued or in progress (pause, exit, mute). */
  cancel(): void;
}

// Pause between the pose-name announcement and "Begin" (which starts the hold
// clock), giving you time to settle into the pose. The clock stays frozen for
// the whole announce → pause → begin span.
export const BEGIN_PAUSE_MS = 3000;

/** "30 seconds", "1 minute", "1 minute and 30 seconds" — reads well via TTS. */
export function spokenDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const parts: string[] = [];
  if (m) parts.push(`${m} minute${m === 1 ? '' : 's'}`);
  if (s) parts.push(`${s} second${s === 1 ? '' : 's'}`);
  return parts.join(' and ') || '0 seconds';
}

/** No-op announcer used when the browser has no speech support. */
export const silentAnnouncer: RoutineAnnouncer = {
  unlock() {},
  prefetch() {},
  announceStep(_cue, onBegin) {
    onBegin?.();
  },
  announceTimeRemaining() {},
  announceCountdown() {},
  announceRelease(onEnd) {
    onEnd?.();
  },
  announceComplete() {},
  cancel() {},
};

/**
 * Web Speech implementation. Returns null when the API is unavailable so callers
 * can fall back to {@link silentAnnouncer} and hide the audio toggle.
 */
let speechRecoveryBound = false;
export function createWebSpeechAnnouncer(): RoutineAnnouncer | null {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return null;
  const synth = window.speechSynthesis;

  // An iOS interruption (e.g. a notification) can leave the speech engine paused
  // and silent. Nudge it back to life when we regain focus/visibility so cues
  // don't stay dead until the user taps a control. Bound once, app-lifetime.
  if (!speechRecoveryBound) {
    speechRecoveryBound = true;
    const revive = () => {
      try {
        synth.resume();
      } catch {
        /* best effort */
      }
    };
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) revive();
    });
    window.addEventListener('focus', revive);
    window.addEventListener('pageshow', revive);
  }

  // Honor the user's saved voice choice (Settings), falling back to auto-pick.
  // Re-resolved on 'voiceschanged' and before each utterance so a change in
  // Settings takes effect on the next cue without recreating the announcer.
  let voice: SpeechSynthesisVoice | null = null;
  const pickVoice = () => {
    const voices = synth.getVoices();
    if (!voices.length) return; // fires again on 'voiceschanged'
    voice = resolveVoice(voices);
  };
  pickVoice();
  synth.addEventListener?.('voiceschanged', pickVoice);

  // Run `cb` once voices are available (immediately if already loaded). After a
  // cold launch getVoices() is briefly empty, so without this the first cue
  // would speak in the default voice before the chosen one loads in.
  const whenVoicesReady = (cb: () => void) => {
    if (synth.getVoices().length) {
      pickVoice();
      cb();
      return;
    }
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      synth.removeEventListener?.('voiceschanged', onReady);
      pickVoice();
      cb();
    };
    const onReady = () => {
      if (synth.getVoices().length) run();
    };
    synth.addEventListener?.('voiceschanged', onReady);
    setTimeout(run, 1500); // safety: never wait forever
  };

  const utter = (text: string) => {
    pickVoice(); // reflect any Settings change made since the last cue
    const u = new SpeechSynthesisUtterance(text);
    if (voice) u.voice = voice;
    u.rate = 0.95;
    return u;
  };
  const speak = (text: string, interrupt = false) => {
    if (interrupt) synth.cancel();
    synth.speak(utter(text));
  };

  // Pending "Begin" timer + a generation counter so a superseded step (Next,
  // Restart, cancel) can't fire a stale "Begin".
  let beginTimer: ReturnType<typeof setTimeout> | null = null;
  let stepGen = 0;
  const clearBeginTimer = () => {
    if (beginTimer !== null) {
      clearTimeout(beginTimer);
      beginTimer = null;
    }
  };

  return {
    unlock() {
      primeSpeech();
    },
    prefetch() {}, // Web Speech synthesizes on demand — nothing to warm.
    announceStep({ name }, onBegin, pauseMs) {
      const myGen = ++stepGen;
      synth.cancel();
      clearBeginTimer();
      const gap = pauseMs ?? BEGIN_PAUSE_MS;
      // Wait for the voice list before the first cue so it speaks in the chosen
      // voice rather than briefly defaulting. Resolves instantly once loaded.
      whenVoicesReady(() => {
        if (myGen !== stepGen) return; // superseded while waiting for voices
        // Terse intro: just the pose name. The hold duration is conveyed by the
        // "N seconds to go" cues instead.
        const intro = utter(`${name}.`);
        const begin = utter('Begin.');
        // Start the caller's clock exactly as "Begin" is voiced; fire once, and
        // also on error so a failed utterance never strands the timer.
        let fired = false;
        const fire = () => {
          if (fired) return;
          fired = true;
          onBegin?.();
        };
        begin.onstart = fire;
        begin.onerror = fire;
        // After the pose name is spoken, pause (settle into the pose), then say
        // "Begin". Guarded by `myGen` so a newer step can't be overtaken.
        const scheduleBegin = () => {
          if (myGen !== stepGen) return;
          clearBeginTimer();
          beginTimer = setTimeout(() => {
            if (myGen !== stepGen) return;
            synth.speak(begin);
          }, gap);
        };
        intro.onend = scheduleBegin;
        intro.onerror = scheduleBegin;
        synth.speak(intro);
      });
    },
    announceTimeRemaining(seconds) {
      speak(`${seconds} seconds to go.`);
    },
    announceCountdown() {
      speak('Three. Two. One.');
    },
    announceRelease(onEnd) {
      const u = utter('And release.');
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        onEnd?.();
      };
      u.onend = finish;
      u.onerror = finish;
      synth.speak(u);
      // Fallback: a backgrounded tab may never fire onend — don't stall the flow.
      if (onEnd) setTimeout(finish, 2500);
    },
    announceComplete() {
      speak('Routine complete. Great work.', true);
    },
    cancel() {
      stepGen++; // invalidate any pending "Begin"
      clearBeginTimer();
      try {
        synth.cancel();
      } catch {
        /* ignore */
      }
    },
  };
}
